/**
 * PLAN FEATURES HOOK
 * 
 * Provides plan-based feature access checks
 * 
 * Usage:
 *   const { hasFeature, features, loading } = usePlanFeatures();
 *   
 *   if (hasFeature('widgets_access')) {
 *     // Show widgets tab
 *   }
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useSupabaseAuth } from '../lib/useSupabaseAuth';

export const usePlanFeatures = () => {
  const { user } = useSupabaseAuth();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch feature access for company's plan
   */
  const fetchFeatures = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get company ID from auth
      const { data: accessData } = await supabase
        .from('user_access_cache')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!accessData?.company_id) {
        setFeatures({});
        return;
      }

      // Check feature access using database function - single call for all features
      const featureNames = [
        'widgets_access',
        'route_optimizer_access',
        'files_access',
        'inspection_access',
        'before_after_photos_access',
        'marketing_campaigns_access'
      ];

      const { data: featuresData, error: featureError } = await supabase.rpc('get_plan_feature_access', {
        p_company_id: accessData.company_id,
        p_feature_names: featureNames
      });

      if (featureError) {
        console.error('[usePlanFeatures] Error checking features:', featureError);
        // Default all to false on error
        const featuresMap = {};
        featureNames.forEach(name => {
          featuresMap[name] = false;
        });
        setFeatures(featuresMap);
      } else {
        // Convert JSONB object to plain object
        const featuresMap = {};
        featureNames.forEach(name => {
          featuresMap[name] = featuresData?.[name] === true;
        });
        setFeatures(featuresMap);
      }
    } catch (err) {
      console.error('[usePlanFeatures] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Check if company has access to a specific feature
   */
  const hasFeature = useCallback(
    (featureName) => {
      return features[featureName] === true;
    },
    [features]
  );

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  return {
    features,
    hasFeature,
    loading,
    error,
    refetch: fetchFeatures,
  };
};

export default usePlanFeatures;

